import Image from "next/image"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import PlaceholderImage from "@modules/common/icons/placeholder-image"
import { clx } from "@modules/common/components/ui"
import AmzPrice from "./price"

export type ProductCardProps = {
  handle: string
  title: string
  thumbnail?: string | null
  amount?: number | null
  compareAt?: number | null
  currencyCode: string
  /** Category name shown as the technical kicker above the title */
  category?: string | null
  rating?: number | null
  reviews?: number | null
  variant?: "grid" | "rail"
}

// Spec-sheet card: product on a high-key pedestal, mono kicker, display title and price. Meant to sit in a grid whose cells
// share hairline borders (see ProductGrid).
const ProductCard = ({
  handle,
  title,
  thumbnail,
  amount,
  compareAt,
  currencyCode,
  category,
  variant = "grid",
}: ProductCardProps) => {
  const discount =
    amount && compareAt && compareAt > amount
      ? Math.round((1 - amount / compareAt) * 100)
      : null

  return (
    <LocalizedClientLink
      href={`/products/${handle}`}
      className={clx("group relative flex flex-col bg-white h-full", {
        "border border-zl-line": variant === "rail",
      })}
      data-testid="product-wrapper"
    >
      <div className="relative w-full aspect-square bg-zl-canvas overflow-hidden">
        {thumbnail ? (
          <Image
            src={thumbnail}
            alt={title}
            fill
            sizes="(max-width: 768px) 50vw, 25vw"
            className="object-contain p-6 small:p-10 mix-blend-multiply group-hover:scale-[1.03] transition-transform duration-500 ease-out"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <PlaceholderImage size={32} />
          </div>
        )}
        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
          {discount ? (
            <span className="zl-badge bg-zl-red text-white px-1.5 py-1">-{discount}%</span>
          ) : null}
        </div>
        <span
          className="absolute left-0 bottom-0 h-0.5 w-0 bg-zl-red group-hover:w-full transition-[width] duration-300 ease-out"
          aria-hidden
        />
      </div>
      <div className="flex flex-col gap-y-2 p-4 border-t border-zl-line flex-1">
        {category && <span className="zl-badge text-zl-steel">{category}</span>}
        <h3
          className="font-display text-base small:text-lg font-medium leading-6 tracking-[-0.01em] text-zl-ink line-clamp-2 group-hover:text-zl-red transition-colors duration-100"
          data-testid="product-title"
        >
          {title}
        </h3>
        {amount !== undefined && amount !== null && (
          <div className="mt-auto pt-2 flex flex-wrap items-baseline gap-x-2" data-testid="price">
            <AmzPrice amount={amount} currencyCode={currencyCode} size="sm" />
            {compareAt && compareAt > amount && (
              <span className="font-mono text-xs text-zl-mute line-through" data-testid="original-price">
                {compareAt.toLocaleString("es-ES", {
                  style: "currency",
                  currency: currencyCode.toUpperCase(),
                })}
              </span>
            )}
          </div>
        )}
      </div>
    </LocalizedClientLink>
  )
}

export default ProductCard
